import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { Department, StaffMember, TimesheetEntry } from '../../types/timesheets';

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type Tab = 'entries' | 'by-department' | 'by-staff' | 'matrix';

interface EntryFormTarget {
  entry: TimesheetEntry | null;
}

function AdminEntryModal({
  entry,
  departments,
  staff,
  onClose,
}: {
  entry: TimesheetEntry | null;
  departments: Department[];
  staff: StaffMember[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState(entry?.user_id ?? staff[0]?.id ?? '');
  const [departmentId, setDepartmentId] = useState(entry?.department_id ?? departments[0]?.id ?? '');
  const [workDate, setWorkDate] = useState(entry?.work_date ?? todayISO());
  const [hours, setHours] = useState(entry ? String(entry.hours) : '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = { departmentId, workDate, hours: Number(hours), notes: notes || null };
      return entry
        ? api.updateTimesheetEntry(entry.id, payload)
        : api.createTimesheetEntry({ ...payload, userId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'timesheets'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? 'Could not save entry'),
  });

  const hoursNum = Number(hours);
  const valid = (entry || userId) && departmentId && workDate && hoursNum > 0 && hoursNum <= 24;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{entry ? 'Edit Entry' : 'Log Hours for Staff'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XCircleIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!entry && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">Staff member *</label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none">
                <option value="" disabled>Select…</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name ?? s.email}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Department *</label>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none">
              <option value="" disabled>Select…</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Date *</label>
              <input type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Hours *</label>
              <input type="number" min="0.25" max="24" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)}
                placeholder="3.5"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none resize-none" />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!valid || save.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors">
            {save.isPending ? 'Saving…' : entry ? 'Save Changes' : 'Log Hours'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminTimesheetsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('entries');
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [userId, setUserId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [modal, setModal] = useState<EntryFormTarget | null>(null);

  const { data: departments = [] } = useQuery({ queryKey: ['departments', 'all'], queryFn: () => api.listDepartments(true) });
  const { data: staff = [] } = useQuery({ queryKey: ['admin', 'staff'], queryFn: () => api.adminListStaff() });

  const period = { from, to };

  const entriesQuery = useQuery({
    queryKey: ['admin', 'timesheets', 'entries', { from, to, userId, departmentId }],
    queryFn: () => api.adminListTimesheetEntries({ from, to, userId: userId || undefined, departmentId: departmentId || undefined }),
    enabled: tab === 'entries',
  });
  const byDeptQuery = useQuery({
    queryKey: ['admin', 'timesheets', 'by-department', period],
    queryFn: () => api.adminTimesheetReportByDepartment(period),
    enabled: tab === 'by-department',
  });
  const byStaffQuery = useQuery({
    queryKey: ['admin', 'timesheets', 'by-staff', period],
    queryFn: () => api.adminTimesheetReportByStaff(period),
    enabled: tab === 'by-staff',
  });
  const matrixQuery = useQuery({
    queryKey: ['admin', 'timesheets', 'matrix', period],
    queryFn: () => api.adminTimesheetReportMatrix(period),
    enabled: tab === 'matrix',
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTimesheetEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'timesheets'] }),
  });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'entries', label: 'Entries' },
    { key: 'by-department', label: 'By Department' },
    { key: 'by-staff', label: 'By Staff' },
    { key: 'matrix', label: 'Person × Department' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Timesheets</h1>
          <p className="text-slate-400 mt-1">All logged hours and cost reporting across departments and staff.</p>
        </div>
        <button onClick={() => setModal({ entry: null })} disabled={staff.length === 0 || departments.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
          <PlusIcon className="w-4 h-4" /> Log Hours for Staff
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-slate-400 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
        </div>
        {tab === 'entries' && (
          <>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Staff</label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)}
                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none">
                <option value="">All staff</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name ?? s.email}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Department</label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}
                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none">
                <option value="">All departments</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-700">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-slate-400 hover:text-white'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'entries' && (
        <EntriesTable
          loading={entriesQuery.isLoading}
          entries={entriesQuery.data ?? []}
          onEdit={(e) => setModal({ entry: e })}
          onDelete={(id) => { if (confirm('Delete this entry?')) deleteMutation.mutate(id); }}
        />
      )}
      {tab === 'by-department' && <ReportTable loading={byDeptQuery.isLoading} rows={byDeptQuery.data ?? []} labelHeader="Department"
        getLabel={(r) => r.department_name} />}
      {tab === 'by-staff' && <ReportTable loading={byStaffQuery.isLoading} rows={byStaffQuery.data ?? []} labelHeader="Staff member"
        getLabel={(r) => r.user_name ?? r.user_email} />}
      {tab === 'matrix' && <MatrixTable loading={matrixQuery.isLoading} rows={matrixQuery.data ?? []} />}

      {modal && (
        <AdminEntryModal entry={modal.entry} departments={departments} staff={staff}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function EntriesTable({ loading, entries, onEdit, onDelete }: {
  loading: boolean;
  entries: TimesheetEntry[];
  onEdit: (e: TimesheetEntry) => void;
  onDelete: (id: string) => void;
}) {
  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;
  if (entries.length === 0) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
        <ClockIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400">No entries for this filter.</p>
      </div>
    );
  }
  const totalHours = entries.reduce((s, e) => s + Number(e.hours), 0);
  const totalCost = entries.reduce((s, e) => s + e.cost_cents, 0);
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-slate-400 font-medium">Date</th>
            <th className="text-left px-4 py-3 text-slate-400 font-medium">Staff</th>
            <th className="text-left px-4 py-3 text-slate-400 font-medium">Department</th>
            <th className="text-right px-4 py-3 text-slate-400 font-medium">Hours</th>
            <th className="text-right px-4 py-3 text-slate-400 font-medium">Cost</th>
            <th className="text-left px-4 py-3 text-slate-400 font-medium">Notes</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {entries.map((e) => (
            <tr key={e.id} className="hover:bg-slate-700/20 transition-colors">
              <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{e.work_date}</td>
              <td className="px-4 py-3 text-white">{e.user_name ?? e.user_email}</td>
              <td className="px-4 py-3 text-slate-300">{e.department_name}</td>
              <td className="px-4 py-3 text-right text-slate-300">{Number(e.hours).toFixed(2)}</td>
              <td className="px-4 py-3 text-right text-slate-300">{e.hourly_rate_cents_snapshot == null ? '—' : money(e.cost_cents)}</td>
              <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{e.notes ?? '—'}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={() => onEdit(e)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors" title="Edit">
                    <PencilIcon className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(e.id)} className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors" title="Delete">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-700 font-medium text-white">
            <td className="px-4 py-3" colSpan={3}>Total</td>
            <td className="px-4 py-3 text-right">{totalHours.toFixed(2)}</td>
            <td className="px-4 py-3 text-right">{money(totalCost)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

interface ReportRowLike { total_hours: number; total_cost_cents: number; entry_count: number }

function ReportTable<T extends ReportRowLike>({ loading, rows, labelHeader, getLabel }: {
  loading: boolean;
  rows: T[];
  labelHeader: string;
  getLabel: (r: T) => string;
}) {
  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
        <p className="text-slate-400">No data for this period.</p>
      </div>
    );
  }
  const totalHours = rows.reduce((s, r) => s + r.total_hours, 0);
  const totalCost = rows.reduce((s, r) => s + r.total_cost_cents, 0);
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-slate-400 font-medium">{labelHeader}</th>
            <th className="text-right px-4 py-3 text-slate-400 font-medium">Entries</th>
            <th className="text-right px-4 py-3 text-slate-400 font-medium">Hours</th>
            <th className="text-right px-4 py-3 text-slate-400 font-medium">Cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-slate-700/20 transition-colors">
              <td className="px-4 py-3 text-white">{getLabel(r)}</td>
              <td className="px-4 py-3 text-right text-slate-300">{r.entry_count}</td>
              <td className="px-4 py-3 text-right text-slate-300">{r.total_hours.toFixed(2)}</td>
              <td className="px-4 py-3 text-right text-slate-300">{money(r.total_cost_cents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-700 font-medium text-white">
            <td className="px-4 py-3">Total</td>
            <td className="px-4 py-3"></td>
            <td className="px-4 py-3 text-right">{totalHours.toFixed(2)}</td>
            <td className="px-4 py-3 text-right">{money(totalCost)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function MatrixTable({ loading, rows }: { loading: boolean; rows: import('../../types/timesheets').MatrixReportRow[] }) {
  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
        <p className="text-slate-400">No data for this period.</p>
      </div>
    );
  }

  // Pivot into person (rows) × department (columns) of hours.
  const people = new Map<string, { label: string; total: number }>();
  const depts = new Map<string, string>();
  const cell = new Map<string, number>(); // `${userId}|${deptId}` -> hours
  for (const r of rows) {
    if (!people.has(r.user_id)) people.set(r.user_id, { label: r.user_name ?? r.user_email, total: 0 });
    people.get(r.user_id)!.total += r.total_hours;
    depts.set(r.department_id, r.department_name);
    cell.set(`${r.user_id}|${r.department_id}`, r.total_hours);
  }
  const deptList = Array.from(depts.entries());

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-slate-400 font-medium sticky left-0 bg-slate-800/50">Staff</th>
            {deptList.map(([id, name]) => (
              <th key={id} className="text-right px-4 py-3 text-slate-400 font-medium whitespace-nowrap">{name}</th>
            ))}
            <th className="text-right px-4 py-3 text-slate-400 font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {Array.from(people.entries()).map(([userId, p]) => (
            <tr key={userId} className="hover:bg-slate-700/20 transition-colors">
              <td className="px-4 py-3 text-white sticky left-0 bg-slate-800/50">{p.label}</td>
              {deptList.map(([deptId]) => {
                const h = cell.get(`${userId}|${deptId}`);
                return <td key={deptId} className="px-4 py-3 text-right text-slate-300">{h ? h.toFixed(2) : '—'}</td>;
              })}
              <td className="px-4 py-3 text-right font-medium text-white">{p.total.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminTimesheetsPage;
